import { ofType } from 'redux-observable';
import { EMPTY, forkJoin, of, race, ReplaySubject, zip } from 'rxjs';
import { catchError, filter, map, mergeMap, take, tap } from 'rxjs/operators';

import { CSW_UPDATE_FULFILLMENT_PLAN_AND_RATE_FAILURE, CSW_UPDATE_FULFILLMENT_PLAN_AND_RATE_SUCCESS } from './csw.actions';
import { getRateFromFulfillmentPlan } from './csw.api';
import Global from '../global-selectors';
import { convertWeight } from '../../services/unit-services/weights/weight-convert-service';
import { WeightUnit } from '../../enums/weight-unit';
import { enabledShippingServiceRecommendationMapSelector } from '../../fulfillment-planner/rate-recommendation/service-mappings';
import { serviceRequirements } from '../../fulfillment-planner/rate-recommendation/service-requirements';
import { InsuranceProviders } from '../../enums/insurance-providers';
import { isParcelGuardActivated } from '../../services/insurance-service';

const userRateResponse = (action$) => {
  return race(
    action$.pipe(
      ofType(CSW_UPDATE_FULFILLMENT_PLAN_AND_RATE_SUCCESS),
      take(1),
      map(({ rateMeta }) => rateMeta),
      catchError(() => EMPTY),
    ),
    action$.pipe(
      ofType(CSW_UPDATE_FULFILLMENT_PLAN_AND_RATE_FAILURE),
      take(1),
      mergeMap(() => EMPTY),
    ),
  );
};

export const rateComparisonSubject = new ReplaySubject(1);
export const insuranceRateComparisonSubject = new ReplaySubject(1);

const rateComparisonPreReqs = (config, getState, serviceMap) => {
  if (!serviceMap[config.serviceId]) {
    return false;
  }

  if (!config.shipFromId) {
    return false;
  }

  const packages = config.packages || [];
  if (!packages.length) {
    return false;
  }

  const { packageTypeId } = packages[0];
  const { validPackageTypeIds } = serviceMap[config.serviceId];
  const packageType = Global.selectSellerPackageType(getState(), { packages }) || {};
  if (validPackageTypeIds.length && !validPackageTypeIds.includes(packageTypeId) && !packageType.isCustom) {
    return false;
  }

  if (config.isReturn) {
    return false;
  }

  return true;
};

const getApplicableServiceIds = (config, getState, serviceMap) => {
  const serviceIds = serviceMap[config.serviceId].mappedServiceIds || [];

  return serviceIds.reduce((acc, serviceId) => {
    const requirements = serviceRequirements[serviceId];
    if (!requirements) {
      acc.push(serviceId);
      return acc;
    }

    const { needsCustoms, isContinentalUSOnly, requiresPackageWeight, isSinglePackageOnly, maxWeightInPounds } = requirements;

    if (Boolean(config.properties?.needsCustoms) !== Boolean(needsCustoms)) {
      return acc;
    }

    if (isContinentalUSOnly) {
      const { state } = config.shipTo || {};
      if (!state || state === 'AK' || state === 'HI') {
        return acc;
      }
    }
    const packages = config.packages || [];

    if (isSinglePackageOnly && packages.length > 1) {
      return acc;
    }

    if (requiresPackageWeight) {
      if (!packages.some((p = {}) => p.weight?.value)) {
        return acc;
      }
    }

    if (maxWeightInPounds) {
      if (packages.some((p = {}) => convertWeight(p.weight, WeightUnit.Pounds) > maxWeightInPounds)) {
        return acc;
      }
    }

    acc.push(serviceId);
    return acc;
  }, []);
};

export const PO_BOX_WARNING =
  'Address appears to be a PO Box.  Are you sure you want to ship using this Carrier?  Additional fees may apply.';
const hasPOBoxWarning = (warnings) => Array.isArray(warnings) && warnings.find((rw) => rw?.warning === PO_BOX_WARNING);

const getRate$ = (serviceId, shippingConfig, getState) => {
  return of(serviceId).pipe(
    map((_serviceId) => Global.selectSellerServiceById(getState(), { serviceId: _serviceId })),
    map((service) => {
      if (!service) {
        return { service };
      }

      const sellerLabelProviders = Global.selectSellerLabelProviders(getState());
      const defaultProvider = sellerLabelProviders.find((slp) => slp.labelProviderId === service.labelProviderId && slp.isPrimaryAccount);
      return {
        transformedConfig: {
          ...shippingConfig,
          fulfillmentPlanId: 'rate-browser',
          serviceId: service.serviceId,
          sellerLabelProviderId: defaultProvider?.sellerLabelProviderId,
        },
        service: { ...service },
      };
    }),
    mergeMap(({ transformedConfig, service }) => {
      if (!transformedConfig.sellerLabelProviderId || !transformedConfig.serviceId || !service) {
        return of(null);
      }

      return getRateFromFulfillmentPlan(transformedConfig).pipe(
        filter(({ rateWarnings, Errors }) => {
          if (Errors && Errors.length) {
            return false;
          }
          return !rateWarnings || !hasPOBoxWarning(rateWarnings);
        }),
        map((response) => {
          return {
            rates: response,
            service,
          };
        }),
      );
    }),
    catchError(() => of(null)),
  );
};

export const compareRates = (shippingConfig, action$, getState) => {
  const serviceMap = enabledShippingServiceRecommendationMapSelector(getState());
  if (!Object.keys(serviceMap).length) {
    return EMPTY;
  }
  const fulfillmentPlanId = shippingConfig.fulfillmentPlanId;
  rateComparisonSubject.next({
    ratedFulfillmentPlanId: fulfillmentPlanId,
    isComparing: true,
  });

  const userChoiceRateResponse$ = userRateResponse(action$);
  const rateComparison$ = of(shippingConfig).pipe(
    filter((config) => rateComparisonPreReqs(config, getState, serviceMap)),
    map((config) => getApplicableServiceIds(config, getState, serviceMap)),
    map((serviceIds) => serviceIds.map((serviceId) => getRate$(serviceId, shippingConfig, getState))),
    mergeMap((requests$) => {
      return forkJoin(...requests$).pipe(
        map((rateResponses) => {
          return rateResponses.reduce((acc, curr) => {
            const currentRateCost = curr?.rates?.totalCost?.value;
            const lowestRateCost = acc?.rates?.totalCost?.value;

            if (!currentRateCost) {
              return acc;
            }
            if (!lowestRateCost) {
              return curr;
            }

            return currentRateCost < lowestRateCost ? curr : acc;
          }, undefined);
        }),
        filter((rateResponse) => !!rateResponse),
      );
    }),
  );

  return zip(userChoiceRateResponse$, rateComparison$).pipe(
    tap(([userChoiceRates, rateRes]) => {
      const sellerId = Global.selectSellerSellerId(getState());
      const userRate = userChoiceRates?.rate;
      const comparedRates = rateRes?.rates;
      const comparedService = rateRes?.service;

      rateComparisonSubject.next({
        userRates: userRate,
        comparedRates,
        comparedService,
        ratedFulfillmentPlanId: fulfillmentPlanId,
        metadata: { sellerId, originalShippingConfiguration: shippingConfig },
      });
    }),
    mergeMap(() => EMPTY),
  );
};

const parcelGuardPrerequisites = (shippingConfig, getState) => {
  if (shippingConfig.insuranceProvider !== InsuranceProviders.Carrier.name) {
    return false;
  }

  if (!isParcelGuardActivated(getState())) {
    return false;
  }

  return true;
};

export const compareInsuranceRates = (shippingConfig, action$, getState) => {
  const fulfillmentPlanId = shippingConfig.fulfillmentPlanId;
  insuranceRateComparisonSubject.next({
    ratedFulfillmentPlanId: fulfillmentPlanId,
    isComparing: true,
  });

  const userChoiceRateResponse$ = userRateResponse(action$);
  const rateComparison$ = of(shippingConfig).pipe(
    filter(() => parcelGuardPrerequisites(shippingConfig, getState)),
    map(() => ({
      transformedConfig: {
        ...shippingConfig,
        fulfillmentPlanId: 'rate-browser',
        insuranceProvider: InsuranceProviders.ParcelGuard.name,
      },
      insuranceProviderId: InsuranceProviders.ParcelGuard.insuranceProviderId,
    })),
    mergeMap(({ transformedConfig, insuranceProviderId }) => {
      return getRateFromFulfillmentPlan(transformedConfig).pipe(
        filter(({ rateWarnings, Errors }) => {
          if (Errors && Errors.length) {
            return false;
          }
          return !rateWarnings || !hasPOBoxWarning(rateWarnings);
        }),
        map((rateResponse) => {
          return {
            rate: rateResponse,
            insuranceProviderId,
          };
        }),
      );
    }),
    catchError(() => EMPTY),
  );

  return zip(userChoiceRateResponse$, rateComparison$).pipe(
    tap(([userChoiceRates, rateComparison]) => {
      const sellerId = Global.selectSellerSellerId(getState());
      const userRate = userChoiceRates?.rate;
      const comparedRate = rateComparison?.rate;

      insuranceRateComparisonSubject.next({
        userRates: userRate,
        comparedRates: comparedRate,
        comparedInsuranceProviderId: rateComparison.insuranceProviderId,
        ratedFulfillmentPlanId: fulfillmentPlanId,
        metadata: { sellerId, originalShippingConfiguration: shippingConfig },
      });
    }),
    mergeMap(() => EMPTY),
  );
};
